import React from "react";

/**
 * Reusable illustrative SVG step graphics – mimic a browser / phone screenshot
 * with red arrow / red box highlighting elements. They're stylised but visually
 * clear, so we don't need to hotlink external assets.
 */

const ArrowRed = ({ x1, y1, x2, y2 }) => (
    <g>
        <defs>
            <marker
                id="arrowhead"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
            >
                <path d="M0,0 L10,5 L0,10 z" fill="#ff3333" />
            </marker>
        </defs>
        <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#ff3333"
            strokeWidth="2.5"
            markerEnd="url(#arrowhead)"
        />
    </g>
);

/* --- Browser window frame --- */
const BrowserFrame = ({ children }) => (
    <svg viewBox="0 0 400 260" className="w-full h-full">
        <rect width="400" height="260" rx="6" fill="#0a0a0a" />
        {/* top bar */}
        <rect width="400" height="34" fill="#1a1a1c" rx="6" />
        <circle cx="14" cy="17" r="4" fill="#ff5f57" />
        <circle cx="28" cy="17" r="4" fill="#febc2e" />
        <circle cx="42" cy="17" r="4" fill="#28c840" />
        <rect x="60" y="8" width="280" height="18" rx="9" fill="#2a2a2c" />
        {children}
    </svg>
);

/* --- Phone frame --- */
const PhoneFrame = ({ children, screenColor = "#0a0a0a" }) => (
    <svg viewBox="0 0 220 380" className="w-full h-full">
        <rect width="220" height="380" rx="32" fill="#1a1a1c" />
        <rect x="6" y="6" width="208" height="368" rx="26" fill={screenColor} />
        <rect x="90" y="12" width="40" height="6" rx="3" fill="#0a0a0a" />
        {children}
    </svg>
);

const StepLabel = ({ children, x = 200, y = 250 }) => (
    <text
        x={x}
        y={y}
        textAnchor="middle"
        fill="#D4AF37"
        fontSize="14"
        fontFamily="Inter, sans-serif"
        fontWeight="600"
    >
        {children}
    </text>
);

/* ----- Desktop step 1: browser with empty url bar + red arrow ----- */
const Desk1 = () => (
    <BrowserFrame>
        <text x="200" y="140" textAnchor="middle" fill="#fff" fontSize="34" fontWeight="700" fontFamily="Inter">
            Google
        </text>
        <rect x="100" y="160" width="200" height="22" rx="11" fill="#1a1a1c" stroke="#2a2a2c" />
        <ArrowRed x1={150} y1={130} x2={180} y2={20} />
        <text x="50" y="22" fill="#D4AF37" fontSize="9" fontWeight="600">casinobiat.online</text>
    </BrowserFrame>
);

/* ----- Desktop step 2: browser with bookmark dragged ----- */
const Desk2 = () => (
    <BrowserFrame>
        <rect x="0" y="34" width="400" height="22" fill="#16161a" />
        <text x="200" y="100" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="700">
            CasinoBiat
        </text>
        <text x="200" y="120" textAnchor="middle" fill="#fff" fontSize="9">
            Mobil Uygulama artık Masaüstünde
        </text>
        {/* big card mockup */}
        <rect x="60" y="140" width="120" height="80" rx="6" fill="#1a1a1c" stroke="#D4AF37" />
        <text x="120" y="170" textAnchor="middle" fill="#D4AF37" fontSize="11" fontWeight="700">
            Favorilerim
        </text>
        <text x="120" y="190" textAnchor="middle" fill="#D4AF37" fontSize="9">
            ÇUBUĞUNA SÜRÜKLE
        </text>
        {/* small monitor mockup right */}
        <rect x="220" y="120" width="140" height="90" rx="4" fill="#0a0a0a" stroke="#2a2a2c" />
        <rect x="225" y="125" width="130" height="70" fill="#1a1a1c" />
        <text x="290" y="160" textAnchor="middle" fill="#D4AF37" fontSize="10" fontWeight="700">
            CASINOBIAT
        </text>
        <ArrowRed x1={70} y1={50} x2={100} y2={150} />
        <rect x="44" y="40" width="14" height="14" stroke="#ff3333" strokeWidth="2" fill="none" />
    </BrowserFrame>
);

/* ----- iOS browser steps ----- */
const Ios1 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="30" rx="6" fill="#2a2a2c" />
        <text x="110" y="60" textAnchor="middle" fill="#fff" fontSize="11">
            Safari
        </text>
        <circle cx="110" cy="180" r="48" fill="#1a1a1c" />
        <path d="M85 180 a25 25 0 0 0 50 0 a25 25 0 0 0 -50 0 z" fill="#D4AF37" />
        <text x="110" y="184" textAnchor="middle" fill="#000" fontSize="12" fontWeight="700">
            Safari
        </text>
        <ArrowRed x1={170} y1={180} x2={140} y2={180} />
    </PhoneFrame>
);
const Ios2 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="28" rx="14" fill="#1a1a1c" />
        <text x="110" y="58" textAnchor="middle" fill="#D4AF37" fontSize="11" fontWeight="700">
            casinobiat.online
        </text>
        <ArrowRed x1={110} y1={110} x2={110} y2={75} />
    </PhoneFrame>
);
const Ios3 = () => (
    <PhoneFrame>
        <text x="110" y="80" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700">
            CasinoBiat
        </text>
        <rect x="40" y="150" width="140" height="40" rx="20" fill="#D4AF37" />
        <text x="110" y="174" textAnchor="middle" fill="#000" fontSize="12" fontWeight="700">
            Siteye Git
        </text>
        <ArrowRed x1={110} y1={230} x2={110} y2={195} />
    </PhoneFrame>
);
const Ios4 = () => (
    <PhoneFrame>
        <text x="110" y="80" textAnchor="middle" fill="#fff" fontSize="11">
            Sayfa Aç
        </text>
        <rect x="40" y="320" width="140" height="40" rx="6" fill="#1a1a1c" />
        {/* share icon */}
        <g transform="translate(95 332)">
            <rect width="14" height="18" rx="2" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
            <line x1="7" y1="0" x2="7" y2="12" stroke="#D4AF37" strokeWidth="1.5" />
            <polyline points="3,4 7,0 11,4" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
        </g>
        <ArrowRed x1={110} y1={300} x2={110} y2={325} />
    </PhoneFrame>
);
const Ios5 = () => (
    <PhoneFrame>
        <rect x="20" y="120" width="180" height="40" rx="8" fill="#1a1a1c" />
        <text x="110" y="145" textAnchor="middle" fill="#fff" fontSize="11">
            Ana Ekrana Ekle
        </text>
        <rect x="170" y="128" width="22" height="22" rx="4" fill="#D4AF37" />
        <text x="181" y="144" textAnchor="middle" fill="#000" fontSize="14" fontWeight="700">+</text>
        <ArrowRed x1={110} y1={200} x2={140} y2={150} />
    </PhoneFrame>
);
const Ios6 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="60" rx="6" fill="#1a1a1c" />
        <text x="110" y="65" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700">
            CasinoBiat
        </text>
        <text x="110" y="82" textAnchor="middle" fill="#888" fontSize="9">
            casinobiat.online
        </text>
        <rect x="140" y="20" width="50" height="20" rx="10" fill="#D4AF37" />
        <text x="165" y="34" textAnchor="middle" fill="#000" fontSize="10" fontWeight="700">Ekle</text>
        <ArrowRed x1={165} y1={70} x2={165} y2={42} />
    </PhoneFrame>
);
const Ios7 = () => (
    <PhoneFrame screenColor="#000">
        {/* Home screen with app icons */}
        <g transform="translate(20 50)">
            {[0, 1, 2, 3].map((row) =>
                [0, 1, 2, 3].map((col) => {
                    const i = row * 4 + col;
                    const isBiat = i === 5;
                    return (
                        <g key={i} transform={`translate(${col * 45} ${row * 55})`}>
                            <rect width="36" height="36" rx="8" fill={isBiat ? "#D4AF37" : "#222"} />
                            {isBiat && (
                                <text x="18" y="22" textAnchor="middle" fill="#000" fontSize="9" fontWeight="800">
                                    CB
                                </text>
                            )}
                            <text x="18" y="48" textAnchor="middle" fill="#888" fontSize="6">
                                {isBiat ? "CasinoBiat" : ""}
                            </text>
                        </g>
                    );
                }),
            )}
        </g>
        <ArrowRed x1={170} y1={250} x2={130} y2={130} />
    </PhoneFrame>
);

/* ----- Android steps (similar style) ----- */
const And1 = () => (
    <PhoneFrame>
        <circle cx="110" cy="180" r="40" fill="#1a1a1c" />
        <g transform="translate(85 155)">
            {/* simplified Chrome logo */}
            <circle cx="25" cy="25" r="24" fill="#fff" />
            <path d="M25 5 a20 20 0 0 1 17 10 H25 z" fill="#ea4335" />
            <path d="M25 5 a20 20 0 0 0 -17 10 l9 16 z" fill="#fbbc04" />
            <path d="M42 15 a20 20 0 0 1 -8 26 l-9 -16 z" fill="#34a853" />
            <circle cx="25" cy="25" r="8" fill="#1a73e8" />
        </g>
        <text x="110" y="240" textAnchor="middle" fill="#fff" fontSize="10">Chrome</text>
        <ArrowRed x1={170} y1={180} x2={140} y2={180} />
    </PhoneFrame>
);
const And2 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="30" rx="6" fill="#1a1a1c" />
        <text x="110" y="60" textAnchor="middle" fill="#D4AF37" fontSize="11" fontWeight="700">
            casinobiat.online
        </text>
        <ArrowRed x1={110} y1={110} x2={110} y2={75} />
    </PhoneFrame>
);
const And3 = () => (
    <PhoneFrame>
        <text x="110" y="80" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700">
            CasinoBiat
        </text>
        <rect x="40" y="150" width="140" height="40" rx="20" fill="#D4AF37" />
        <text x="110" y="174" textAnchor="middle" fill="#000" fontSize="12" fontWeight="700">
            Siteye Git
        </text>
        <ArrowRed x1={110} y1={230} x2={110} y2={195} />
    </PhoneFrame>
);
const And4 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="28" rx="4" fill="#1a1a1c" />
        <g transform="translate(178 47)" fill="#D4AF37">
            <circle cx="0" cy="0" r="2" />
            <circle cx="0" cy="7" r="2" />
            <circle cx="0" cy="14" r="2" />
        </g>
        <ArrowRed x1={120} y1={55} x2={170} y2={54} />
    </PhoneFrame>
);
const And5 = () => (
    <PhoneFrame>
        <rect x="20" y="80" width="180" height="42" rx="6" fill="#1a1a1c" />
        <text x="110" y="105" textAnchor="middle" fill="#fff" fontSize="11">
            Ana Ekrana Ekle
        </text>
        <ArrowRed x1={140} y1={160} x2={140} y2={120} />
    </PhoneFrame>
);
const And6 = () => (
    <PhoneFrame>
        <rect x="20" y="40" width="180" height="70" rx="6" fill="#1a1a1c" />
        <text x="110" y="65" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700">
            CasinoBiat
        </text>
        <text x="110" y="82" textAnchor="middle" fill="#888" fontSize="9">
            Ana ekrana eklensin mi?
        </text>
        <rect x="135" y="86" width="55" height="20" rx="10" fill="#D4AF37" />
        <text x="162" y="100" textAnchor="middle" fill="#000" fontSize="10" fontWeight="700">Ekle</text>
        <ArrowRed x1={162} y1={140} x2={162} y2={108} />
    </PhoneFrame>
);
const And7 = () => (
    <PhoneFrame>
        <rect x="20" y="80" width="180" height="60" rx="6" fill="#1a1a1c" />
        <text x="110" y="105" textAnchor="middle" fill="#fff" fontSize="11">
            Onayla
        </text>
        <rect x="135" y="115" width="55" height="20" rx="10" fill="#D4AF37" />
        <text x="162" y="129" textAnchor="middle" fill="#000" fontSize="10" fontWeight="700">Ekle</text>
        <ArrowRed x1={162} y1={170} x2={162} y2={138} />
    </PhoneFrame>
);
const And8 = () => (
    <PhoneFrame screenColor="#000">
        <g transform="translate(20 50)">
            {[0, 1, 2, 3].map((row) =>
                [0, 1, 2, 3].map((col) => {
                    const i = row * 4 + col;
                    const isBiat = i === 5;
                    return (
                        <g key={i} transform={`translate(${col * 45} ${row * 55})`}>
                            <rect width="36" height="36" rx="8" fill={isBiat ? "#D4AF37" : "#222"} />
                            {isBiat && (
                                <text x="18" y="22" textAnchor="middle" fill="#000" fontSize="9" fontWeight="800">
                                    CB
                                </text>
                            )}
                            <text x="18" y="48" textAnchor="middle" fill="#888" fontSize="6">
                                {isBiat ? "CasinoBiat" : ""}
                            </text>
                        </g>
                    );
                }),
            )}
        </g>
        <ArrowRed x1={170} y1={250} x2={130} y2={130} />
    </PhoneFrame>
);

const MAP = {
    "desk-1": Desk1,
    "desk-2": Desk2,
    "ios-1": Ios1,
    "ios-2": Ios2,
    "ios-3": Ios3,
    "ios-4": Ios4,
    "ios-5": Ios5,
    "ios-6": Ios6,
    "ios-7": Ios7,
    "and-1": And1,
    "and-2": And2,
    "and-3": And3,
    "and-4": And4,
    "and-5": And5,
    "and-6": And6,
    "and-7": And7,
    "and-8": And8,
};

export const StepIllustration = ({ name }) => {
    const Comp = MAP[name];
    if (!Comp) return null;
    return (
        <div className="step-img w-full aspect-[4/3] flex items-center justify-center p-1">
            <Comp />
        </div>
    );
};
